import { NgIf } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, ViewChild, ViewEncapsulation, WritableSignal, inject } from '@angular/core';
import { FormsModule, NgForm, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { fuseAnimations } from '@fuse/animations';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { AuthService } from 'app/core/auth/auth.service';
import { GlobalService } from 'app/shared/services/global.service';
import { Observable, catchError, finalize, of } from 'rxjs';

@Component({
    selector     : 'forgot-password-form',
    templateUrl  : './forgot-password-form.component.html',
    styleUrls    : ['./forgot-password-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations   : fuseAnimations,
    imports      : [NgIf, FuseAlertComponent, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatProgressSpinnerModule],
})
export class ForgotPasswordFormComponent implements OnInit
{
    @ViewChild('forgotPasswordNgForm') forgotPasswordNgForm: NgForm;
    @Output() successEvent: EventEmitter<any> = new EventEmitter<any>();
    @Output() errorEvent: EventEmitter<any> = new EventEmitter<any>();
    @Output() goBackEvent: EventEmitter<any> = new EventEmitter<any>();

    alert: { type: FuseAlertType; message: string } = {
        type   : 'success',
        message: '',
    };
    private _globalService = inject(GlobalService);
    forgotPasswordForm: UntypedFormGroup;
    showAlert: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _authService: AuthService,
        private _formBuilder: UntypedFormBuilder,
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Create the form
        this.forgotPasswordForm = this._formBuilder.group({
            username: ['', [Validators.required]],
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Send the reset link
     */
    sendResetLink(): void
    {
       // this.checkNetworkConnectivity();
        this._globalService.verifyConnectivity().subscribe();

        // Return if the form is invalid
        if ( this.forgotPasswordForm.invalid )
        {
            return;
        }

        // Disable the form
        this.forgotPasswordForm.disable();

        // Hide the alert
        this.showAlert = false;

        // Forgot password
        this._authService.forgotPassword(this.forgotPasswordForm.get('username').value)
            .pipe(
                finalize(() =>
                {
                    // Re-enable the form
                    this.forgotPasswordForm.enable();

                    // Reset the form
                    this.forgotPasswordNgForm.resetForm();

                    // Show the alert
                    this.showAlert = true;
                }),
            )
            .subscribe({
                next: () =>
                {
                    // Set the success alert when api returned successfully and online is true
                    if (this._globalService.isOnline()) {
                        this.alert = {
                            type: 'success',
                            message: 'Password reset sent! You\'ll receive an email if you are registered on our system.',
                        };
                    } else {
                        // Set the error alert when online is false
                        this.alert = {
                            type: 'error',
                            message: 'Something went wrong. Please try again.'
                        };
                    }
                    this.successEvent.emit();
                },
                error: () =>
                {
                    // Set the error alert when api returned an error
                    this.alert = {
                        type: 'error',
                        message: 'Something went wrong. Please try again.'
                    };
                    this.errorEvent.emit();
                },
            });
    }

    goBackToSignIn() {
        this.goBackEvent.emit();
    }
}
